import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users, Clock } from '../components/icons';
import { events } from '../data/events';

const Events = () => {
  const upcomingEvents = events.filter(e => e.upcoming);
  const pastEvents = events.filter(e => !e.upcoming);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section data-hero className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/Events/Event-Hero.jpg"
            alt="Activities & Events"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        <div className="relative z-10 h-full flex items-start justify-center pt-32">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white"
            >
              Activities & Events
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-gray-200 font-light"
            >
              Activities, Events, Partnerships & More.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8EC641] animate-pulse" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Upcoming Events</h2>
            </div>

            <div className="space-y-8">
              {upcomingEvents.map((event) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100"
                >
                  <div className="grid lg:grid-cols-2 gap-0">
                    {/* Designed promo banner (uncropped 16:9) */}
                    <Link to={`/events/${event.id}`} className="relative flex items-center bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-100">
                      {event.bannerImage ? (
                        <img
                          src={event.bannerImage}
                          alt={event.title}
                          className="w-full aspect-video object-cover"
                        />
                      ) : (
                        <div className="w-full aspect-video bg-gradient-to-br from-[#0D56A6] to-[#062f63]" />
                      )}
                      <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
                        <span className="bg-[#8EC641] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide shadow">
                          Coming Soon
                        </span>
                        {event.registrationOpen && (
                          <span className="bg-white/90 backdrop-blur text-[#0D56A6] text-xs font-semibold px-3 py-1 rounded-full shadow">
                            Registration Open
                          </span>
                        )}
                      </div>
                    </Link>

                    {/* Details */}
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">{event.title}</h3>
                      <p className="text-sm text-gray-400 mb-4">{event.partner}</p>
                      <p className="text-gray-600 mb-6 leading-relaxed">{event.description}</p>

                      <div className="space-y-2 mb-8">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Calendar size={16} className="text-[#0D56A6] flex-shrink-0" />
                          {event.date}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <Clock size={16} className="text-[#0D56A6] flex-shrink-0" />
                          {event.time}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <MapPin size={16} className="text-[#0D56A6] flex-shrink-0" />
                          {event.location}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        {event.registrationOpen && (
                          <Link
                            to={`/events/${event.id}/register`}
                            className="inline-flex items-center justify-center gap-2 bg-[#8EC641] hover:bg-[#7ab535] text-white font-semibold px-6 py-3 rounded-xl transition-all hover:-translate-y-0.5 shadow-lg shadow-[#8EC641]/30"
                          >
                            Register Now
                            <ArrowRight size={16} />
                          </Link>
                        )}
                        <Link
                          to={`/events/${event.id}`}
                          className="inline-flex items-center justify-center gap-2 border-2 border-[#0D56A6] text-[#0D56A6] font-semibold px-6 py-3 rounded-xl hover:bg-[#0D56A6] hover:text-white transition-all"
                        >
                          View Programme
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Past Events */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          {pastEvents.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Past Events</h2>
              <p className="text-gray-500 mt-1">A look back at our recent workshops and gatherings.</p>
            </div>
          )}
          <div className="space-y-16">
            {pastEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Left Side - Visuals & Key Info */}
                  <div className="p-8 bg-white border-r border-gray-100">
                    {/* Partner Info */}
                    <div className="mb-6">
                      <p className="text-sm text-gray-500 mb-4">{event.partner}</p>
                    </div>

                    {/* Product Images */}
                    <div className="mb-8">
                      <div className="grid grid-cols-2 gap-4">
                        {event.productImages.map((image, imgIndex) => (
                          <div key={imgIndex} className="relative h-32 rounded-lg overflow-hidden">
                            <img
                              src={image}
                              alt={`${event.product} ${imgIndex + 1}`}
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="space-y-4">
                      <div className="text-sm text-gray-600">{event.phase}</div>
                      <div className="text-2xl font-bold text-[#0D56A6]">{event.product}</div>
                      <div className="flex items-center gap-2">
                        <span className="bg-[#8EC641] text-white text-xs font-bold px-3 py-1 rounded-full">
                          {event.category}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-[#0D56A6]" />
                          {event.date}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-[#0D56A6]" />
                          {event.time}
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="text-[#0D56A6]" />
                          {event.location}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-[#0D56A6]" />
                          {event.attendees} attendees
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Description */}
                  <div className="p-8">
                    <h2 className="text-2xl font-bold text-[#0D56A6] mb-4">
                      {event.title}
                    </h2>
                    <p className="text-gray-700 mb-6 leading-relaxed">
                      {event.description}
                    </p>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                      {event.longDescription}
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link
                        to={`/events/${event.id}`}
                        className="inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                      >
                        Read More
                        <ArrowRight size={16} className="ml-2" />
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] hover:bg-[#0D56A6] hover:text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300"
                      >
                        Register Interest
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mt-16"
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Stay Updated with Our Events
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our mailing list to receive notifications about upcoming workshops, 
              training sessions, and industry events.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Get in Touch
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] hover:bg-[#0D56A6] hover:text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300"
              >
                Learn More About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Events;
